import { flip, offset, shift, useFloating } from '@floating-ui/vue';
import Vue, { defineComponent, h, nextTick, reactive, ref } from 'vue';
import { tooltip } from './CsTooltip';
import { useTimeoutFn } from 'dashboard/composables';

const tooltipTrigger = ref(null);
const tooltipContent = ref(null);
const placement = ref('right');

const tooltipStore = reactive({
  isOpen: false,
  label: '',
});

// Create the floating UI instance only once
const { floatingStyles, update } = useFloating(tooltipTrigger, tooltipContent, {
  placement,
  middleware: [offset(4), flip(), shift()],
});

function useTooltipDirective() {
  const showDelay = ref(300);
  const hideDelay = ref(0);

  const showTooltipFn = useTimeoutFn(
    () => {
      tooltipStore.isOpen = true;
      update();
    },
    () => showDelay.value,
    { immediate: false }
  );

  const hideTooltipFn = useTimeoutFn(
    () => {
      tooltipStore.isOpen = false;
      tooltipStore.label = '';
      // Cleanup state
      placement.value = 'top';
    },
    () => hideDelay.value,
    { immediate: false }
  );

  const onOpen = (el, options) => {
    tooltipTrigger.value = el;

    // Clear any existing hide timeout
    hideTooltipFn.stop();

    // Update label if provided in options
    if (options && options.value) {
      const currentLabel =
        typeof options.value === 'string'
          ? options.value
          : options.value.content || options.value.label;

      if (!currentLabel) return;

      placement.value = options.value?.placement || 'top';
      tooltipStore.label = currentLabel;

      // Update the show delay from options or use default (300ms)
      showDelay.value = options.value?.showDelay || 300;

      // Start the show timeout with the updated delay
      showTooltipFn.clear();
      showTooltipFn.start();
    } else {
      // If no label is provided, make sure the tooltip is closed and no label
      tooltipStore.isOpen = false;
      tooltipStore.label = '';
    }
  };

  const onClose = (el, options) => {
    // Clear any existing show timeout
    showTooltipFn.stop();

    // Update the hide delay from options or use default (0ms - immediate hide)
    hideDelay.value = options?.value?.hideDelay || 0;

    // Start the hide timeout with the updated delay
    hideTooltipFn.stop();
    hideTooltipFn.start();
  };

  const cleanup = () => {
    // Clear any pending timeouts
    showTooltipFn.stop();
    hideTooltipFn.stop();

    onClose();
  };

  return {
    onOpen,
    onClose,
    cleanup,
  };
}

const tooltipContentComponent = defineComponent({
  name: 'TooltipRoot',
  setup() {
    return () => {
      const isHtmlContent = tooltipStore.label.includes('<');

      return h(
        'div',
        {
          ref: tooltipContent,
          class: tooltip().root(),
          style: {
            display: tooltipStore.isOpen ? 'block' : 'none',
            position: 'absolute',
            zIndex: 99999, // Ensure tooltip is on top of any overlay elements
            ...floatingStyles.value,
          },
        },
        [
          h(
            'span',
            {
              class: tooltip().label(),
              ...(isHtmlContent && {
                domProps: {
                  innerHTML: tooltipStore.label,
                },
              }),
            },
            isHtmlContent ? null : tooltipStore.label
          ),
        ]
      );
    };
  },
});

const isBrowser =
  typeof window !== 'undefined' && typeof window.document !== 'undefined';

const TOOLTIP_PORTAL_ID = 'cs-tooltip-portal';
const TOOLTIP_DIRECTIVE_ROOT_ID = 'cs-tooltip-directive';
const VM = typeof window !== 'undefined' && window.Vue ? window.Vue : Vue;

// Create a single instance of the directive
export const tooltipDirective = useTooltipDirective();

function createPortal() {
  if (!isBrowser) return;

  let portalElement;
  const existingPortalElement = document.getElementById(TOOLTIP_PORTAL_ID);

  if (existingPortalElement) {
    portalElement = existingPortalElement;
  } else {
    const el = document.createElement('div');
    el.id = TOOLTIP_PORTAL_ID;
    if (document.body != null) {
      document.body.appendChild(el);
    }
    portalElement = el;
  }

  const root = document.createElement('div');
  root.id = TOOLTIP_DIRECTIVE_ROOT_ID;
  portalElement.appendChild(root);

  // eslint-disable-next-line consistent-return
  return portalElement;
}

function createTooltipInstance() {
  if (!isBrowser) return;

  Vue.nextTick(() => {
    new VM({
      name: 'CommslayerTooltip',
      // eslint-disable-next-line no-shadow
      render: h => h(tooltipContentComponent),
    }).$mount(`#${TOOLTIP_DIRECTIVE_ROOT_ID}`, true);
  });
}

// Initialize tooltip system - ensure both functions are called once and in order
let initialized = false;

// Call initialization once
export function initializeTooltipInstance() {
  if (initialized || !isBrowser) return;

  createPortal();
  createTooltipInstance();

  initialized = true;
}

function applyTooltip(el, options) {
  const { onOpen, onClose } = tooltipDirective;
  el.$pixelTooltipOpenHandler = () => onOpen(el, options);
  el.$pixelTooltipCloseHandler = () => onClose(el, options);

  el.addEventListener('mouseover', el.$pixelTooltipOpenHandler);
  el.addEventListener('focus', el.$pixelTooltipOpenHandler);
  el.addEventListener('mouseleave', el.$pixelTooltipCloseHandler);
  el.addEventListener('blur', el.$pixelTooltipCloseHandler);
}

function removeTooltip(el) {
  // eslint-disable-next-line no-unused-expressions
  el.$pixelTooltipCloseHandler && el.$pixelTooltipCloseHandler();

  el.removeEventListener('mouseover', el.$pixelTooltipOpenHandler);
  el.removeEventListener('focus', el.$pixelTooltipOpenHandler);
  el.removeEventListener('mouseleave', el.$pixelTooltipCloseHandler);
  el.removeEventListener('blur', el.$pixelTooltipCloseHandler);

  delete el.$pixelTooltipOpenHandler;
  delete el.$pixelTooltipCloseHandler;
}

export const vTooltip = {
  bind(el, binding) {
    // Bind directive only if label exist
    if (binding.value) {
      applyTooltip(el, binding);
    }
  },
  componentUpdated(el, binding) {
    removeTooltip(el);

    // Only re-bind if the label exists
    if (binding.value) {
      nextTick(() => {
        applyTooltip(el, binding);
      });
    }
  },
  unbind(el) {
    removeTooltip(el);
  },
};
